import beerIcon from "../assets/page_logo.png";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { businessApi } from "../services/api";
import { AGREEMENT_VERSION } from "../constants/agreement";

// A revision date belongs to the document, not to whoever happens to be reading it.
// This rendered `new Date()` before, so the contract claimed to have been revised
// today, every day (AC-PARTNER-E6).
const AGREEMENT_LAST_UPDATED = "2026-07-20";

function PartnerAgreement() {
  const navigate = useNavigate();
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Render nothing until the server confirms whether this user has already accepted:
  // flashing the agreement at someone who accepted on another device, only to redirect
  // them, is the re-prompt this screen exists to remove.
  const [isChecking, setIsChecking] = useState(true);

  // The screen had no guard at all, so anyone could open it and mint themselves an
  // acceptance record (AC-PARTNER-S3). Checks access_token only, matching
  // /create-profile — a partner whose post-password login failed still holds a valid
  // token but no user_profile, and must be able to finish onboarding. Then resume: if the
  // acceptance is already on record for the current version, skip ahead instead of
  // re-collecting consent — the record lives against the user, not this browser. (#28)
  useEffect(() => {
    if (!localStorage.getItem("access_token")) {
      navigate("/admin-login", { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const status = await businessApi.getAgreementAcceptance();
        if (!cancelled && status?.accepted) {
          navigate("/create-profile", { replace: true });
          return;
        }
      } catch (_) {
        // Can't reach the server: show the agreement rather than block onboarding on a
        // transient read failure — they can still accept, which re-checks server-side.
      }
      if (!cancelled) {
        setIsChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleCancel = () => {
    // Cancel must NOT point at /create-profile any more: that screen now redirects back
    // here whenever the agreement isn't accepted (its mount guard), so the two would
    // ping-pong. /admin-dashboard is no better — a partner with no business is auto-
    // forwarded from the dashboard home straight back into onboarding. Send them to
    // sign-in: a clean exit from setup they can resume by logging back in, and never a
    // dead-end into a signup their email can no longer start (AC-PARTNER-E9). (The old
    // navigate(-1) hit /create-password, whose guard this trip has already invalidated.)
    navigate("/admin-login");
  };

  const handleAccept = async () => {
    if (!isAgreed || isSubmitting) {
      return;
    }

    // Record the acceptance server-side at the moment of consent — before the business
    // exists — keyed to the user so it survives leaving and returning on another device.
    // We BLOCK on failure: a partner must never proceed as "accepted" when no durable
    // record was written. That silent loss (localStorage-only) was the whole defect. The
    // server stamps the time and keeps the first acceptance if this retries. (#28, #29)
    setIsSubmitting(true);
    try {
      await businessApi.recordAgreementAcceptance({ version: AGREEMENT_VERSION });
    } catch (err) {
      setIsSubmitting(false);
      toast.error(
        err?.error || "Could not record your acceptance. Please try again.",
        { position: "top-right", autoClose: 4000 },
      );
      return;
    }

    navigate("/create-profile");
  };

  if (isChecking) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: "#F9F4EA",
        width: "100%",
        // border-box: width:100% plus 20px of side padding was 40px wider than the viewport.
        boxSizing: "border-box",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        padding: "40px 20px",
      }}
    >
      {/* Main Content */}
      <div
        style={{
          maxWidth: "1600px",
          width: "100%",
          margin: "0 auto",
          display: "flex",
          flexWrap: "wrap",
          gap: "clamp(24px, 5vw, 48px)",
          alignItems: "flex-start",
        }}
      >
        {/* Left: Logo */}
        <div
          style={{
            width: "min(500px, 100%)",
            minHeight: "530px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img
              src={beerIcon}
              alt="beer icon"
              style={{ width: "min(400px, 60vw)", height: "auto", cursor: "pointer" }}
              onClick={() => navigate("/admin-login")}
            />
          </div>
        </div>

        {/* Right: Agreement */}
        <div
          style={{
            flex: 1,
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
            padding: "40px",
            maxHeight: "78vh",
            overflowY: "auto",
          }}
        >
          <h2
            style={{
              margin: "0 0 10px",
              fontSize: "38px",
              lineHeight: 1.1,
              color: "#2A2A2A",
            }}
          >
            my HOOM PARTNER PARTICIPATION AGREEMENT
          </h2>

          <p style={{ margin: "0 0 22px", color: "#555", fontSize: "14px" }}>
            Last Updated: {AGREEMENT_LAST_UPDATED}
          </p>

          <div style={{ color: "#303030", fontSize: "15px", lineHeight: 1.6 }}>
            <p style={{ marginTop: 0 }}>
              <strong>Effective Date:</strong> The date you click &quot;I
              Agree&quot; below.
              <br />
              <strong>Between:</strong>
            </p>

            <p style={{ margin: "0 0 6px" }}>
              ● <strong>my HOOM</strong> (&quot;Company,&quot; &quot;we,&quot;
              &quot;us,&quot; or &quot;our&quot;), provider of the my HOOM mobile
              application (&quot;App&quot;); and
            </p>
            <p style={{ margin: "0 0 14px" }}>
              ●{" "}
              <strong>
                You, the partnering establishment or authorized representative
                (&quot;you,&quot; or &quot;your&quot;).
              </strong>
            </p>

            <p>
              By clicking <strong>&quot;I Agree,&quot;</strong> you represent
              that you have the authority to bind your business to this Agreement
              and agree to the following terms:
            </p>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p>
              <strong>1. Purpose</strong>
              <br />
              The App enables users to purchase a prepaid beer (or similar
              beverage) at participating entities for redemption by a designated
              recipient. This Agreement governs your participation as a partner
              in the App.
            </p>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p style={{ marginBottom: "6px" }}>
              <strong>2. Partner Responsibilities</strong>
            </p>
            <p style={{ margin: "0 0 10px" }}>You agree to:</p>
            <ol style={{ marginTop: 0, paddingLeft: "20px" }}>
              <li style={{ marginBottom: "8px" }}>
                <strong>Honor Purchased Beers.</strong> Provide one standard
                pour of the designated beer or equivalent beverage
                (&quot;Redeemed Item&quot;) to a customer presenting a valid
                redemption code or digital voucher via the App.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Compliance with Law.</strong> Comply with all applicable
                federal, state, and local laws, including liquor licensing, age
                verification, and responsible service obligations.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Accurate Information.</strong> Maintain accurate
                business information, hours on the App.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Quality Standards.</strong> Serve beverages consistent
                with your standard quality and size specifications.
              </li>
              <li>
                <strong>No Misuse.</strong> Not use the App to offer or promote
                any illegal, unsafe, or unauthorized activity.
              </li>
            </ol>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p style={{ marginBottom: "6px" }}>
              <strong>3. Payment and Fees</strong>
            </p>
            <ol style={{ marginTop: 0, paddingLeft: "20px" }}>
              <li style={{ marginBottom: "8px" }}>
                <strong>Purchase Price.</strong> When a user buys a beer for
                redemption at your establishment the user&apos;s payment will be
                processed by the Company or its payment processor.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Remittance.</strong> my HOOM.LLC will remit to you the
                net purchase amount (after deducting agreed transaction or
                service fees of 15%) within 30 days following redemption, via
                direct deposit into the participating entity bank account.
              </li>
              <li>
                <strong>Unredeemed Items.</strong> Unredeemed items after 12
                months may be handled according to applicable escheatment or
                unclaimed property laws.
              </li>
            </ol>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p style={{ marginBottom: "6px" }}>
              <strong>4. Term and Termination</strong>
            </p>
            <ol style={{ marginTop: 0, paddingLeft: "20px" }}>
              <li style={{ marginBottom: "8px" }}>
                <strong>Term.</strong> This Agreement begins upon your
                acceptance and continues until terminated by either party.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Termination.</strong> Either party may terminate at any
                time by providing written notice or by deactivating the
                participating partner account.
              </li>
              <li>
                <strong>Effect of Termination.</strong> Upon termination, you
                must honor all outstanding valid vouchers issued prior to
                termination, and we will process related payments according to
                Section 3.
              </li>
            </ol>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p>
              <strong>5. Intellectual Property</strong>
              <br />
              Each party retains ownership of its trademarks, logos, and other
              intellectual property. You grant us a limited, nonexclusive,
              royalty-free right to display your name, logo, and menu items on
              the App and related promotional materials.
            </p>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p style={{ marginBottom: "6px" }}>
              <strong>6. Disclaimers and Limitation of Liability</strong>
            </p>
            <p style={{ margin: "0 0 6px" }}>
              ● The App is provided &quot;as is.&quot; We do not guarantee
              uninterrupted service or any particular volume of sales.
            </p>
            <p style={{ margin: "0 0 6px" }}>
              ● To the maximum extent permitted by law, neither party shall be
              liable for indirect, incidental, or consequential damages.
            </p>
            <p style={{ margin: 0 }}>
              ● Our total liability under this Agreement will not exceed the
              total fees paid to you in the 3 months preceding the claim.
            </p>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p>
              <strong>7. Indemnification</strong>
              <br />
              You agree to indemnify and hold harmless the Company, its
              officers, and affiliates against claims, damages, or losses
              arising out of your (a) violation of law, (b) negligence or
              misconduct, or (c) breach of this Agreement.
            </p>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p>
              <strong>8. Independent Contractor</strong>
              <br />
              You and the Company are independent contractors. Nothing in this
              Agreement creates an agency or joint venture.
            </p>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p>
              <strong>9. Modifications</strong>
              <br />
              We may update this Agreement from time to time. Continued
              participation after notice of a change constitutes acceptance of
              the new terms.
            </p>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p>
              <strong>10. Governing Law</strong>
              <br />
              This Agreement is governed by the laws of the State of [Your
              State], without regard to conflict of laws principles.
            </p>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p>
              <strong>11. Entire Agreement</strong>
              <br />
              This Agreement represents the entire understanding between the
              parties and supersedes all prior agreements or communications
              relating to its subject matter.
            </p>

            <hr
              style={{
                border: "none",
                borderTop: "1px solid #ddd",
                margin: "16px 0",
              }}
            />

            <p>
              <strong>
                By clicking &quot;I Agree,&quot; you acknowledge that you have
                read, understood, and agree to be bound by this Participation
                Agreement.
              </strong>
            </p>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontWeight: 600,
                marginTop: "8px",
              }}
            >
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={(event) => setIsAgreed(event.target.checked)}
              />
              I Agree
            </label>

            <div
              style={{
                marginTop: "24px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  border: "1px solid #c8c8c8",
                  backgroundColor: "#fff",
                  color: "#333",
                  fontWeight: 600,
                  borderRadius: "8px",
                  padding: "10px 20px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAccept}
                disabled={!isAgreed || isSubmitting}
                style={{
                  border: "none",
                  backgroundColor:
                    isAgreed && !isSubmitting ? "#DE810E" : "#D8D8D8",
                  color: "#fff",
                  fontWeight: 700,
                  borderRadius: "8px",
                  padding: "10px 20px",
                  cursor:
                    isAgreed && !isSubmitting ? "pointer" : "not-allowed",
                }}
              >
                {isSubmitting ? "Submitting..." : "Accept"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PartnerAgreement;
